import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Github, Linkedin } from 'lucide-react';
import SEO from '../components/SEO';
import { useProjects } from '../contexts/ProjectsContext';
import ProjectCard from '../components/ProjectCard';
import Profile from '../assets/img/asma1.png';
import { useTranslation } from '../contexts/LanguageContext';

const HomePage: React.FC = () => {
  const { projects, loading } = useProjects();
  const featuredProjects = projects.filter(project => project.featured).slice(0, 3);
  const { t } = useTranslation();

  const socialLinks = [
    { icon: <Github size={24} />, href: 'https://github.com/asmaessafi', label: 'GitHub' },
    { icon: <Linkedin size={24} />, href: 'https://linkedin.com/in/asma-essafi-a60a43331/', label: 'LinkedIn' }
  ];

  return (
    <>
      <SEO
        title={t('home.seo.title')}
        description={t('home.seo.description')}
        ogUrl="/"
      />

      {/* Hero Section */}
      <section className="pt-16 md:pt-24 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-center">
          <div className="lg:col-span-3">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="space-y-6"
            >
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight">
                {t('home.hero.headingPrefix')}{' '}
                <span className="text-sky-600">ASMA ESSAFI</span>
                <br />
                {t('home.hero.role')}
              </h1>
              <p className="text-lg text-white max-w-2xl">
                {t('home.hero.description.line1')}{' '}
                {t('home.hero.description.line2')}
              </p>

              <div className="flex flex-wrap gap-4">

                <Link
                  to="/projects"
                  className="px-6 py-3 font-medium transition-colors flex items-center"
                >
                  <button
                    className="bg-gradient-to-r from-sky-600 via-sky-500 to-sky-700 hover:from-sky-700 hover:via-sky-600 hover:to-sky-800 text-white font-bold px-10 py-5 rounded-2xl shadow-xl shadow-blue-500/25 hover:shadow-blue-500/40 hover:scale-[1.03] active:scale-95 transition-all duration-500 group text-lg flex items-center mx-auto">
                    {t('home.hero.cta.viewWork')}
                    <ArrowRight className="ml-4 h-6 w-6 transition-transform group-hover:translate-x-2 drop-shadow-[0_0_8px_rgba(255,255,255,0.5)]" />
                  </button>

                </Link>
                <Link
                  to="/contact"
                  className="px-6 py-3 text-white font-medium rounded-lg "
                >
                  <div className="relative inline-flex  group">
                    <div
                      className="absolute transitiona-all duration-1000 opacity-70 -inset-px bg-gradient-to-r from-sky-600 via-sky-500 to-sky-700 hover:from-sky-700 hover:via-sky-600 hover:to-sky-800 rounded-xl blur-lg group-hover:opacity-100 group-hover:-inset-1 group-hover:duration-200 ">
                    </div>
                    <button
                      className="relative inline-flex items-center justify-center px-8 py-4 text-lg hover:scale-[1.03] font-bold text-white transition-all duration-200 bg-transparent  rounded-xl"
                      role="button">{t('home.hero.cta.contact')}
                    </button>
                  </div>
                </Link>
              </div>

              <div className="flex space-x-4 pt-2">
                {socialLinks.map((link, index) => (
                  <motion.a
                    key={index}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sky-600 hover:text-white transition-colors"
                    aria-label={link.label}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {link.icon}
                  </motion.a>
                ))}
              </div>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="lg:col-span-2"
          >
            <div className="relative">
              <div className="w-full h-[500px] rounded-2xl overflow-hidden">
                <img
                  src={Profile}
                  alt="Profile"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 w-48 h-48 bg-sky-500 rounded-full mix-blend-multiply filter blur-2xl opacity-60 animate-blob"></div>
              <div className="absolute -top-6 -right-6 w-48 h-48 bg-primary-500 rounded-full mix-blend-multiply filter blur-2xl opacity-60 animate-blob animation-delay-2000"></div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Featured Projects Section */}
      <section className="py-16 bg-opacity-15 bg-sky-800 rounded-2xl shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-sky-600 mb-4">
              {t('home.featured.title')}
            </h2>
            <p className="text-white max-w-2xl mx-auto">
              {t('home.featured.description')}
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-sky-600"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {featuredProjects.map((project, index) => (
                <ProjectCard key={project.id} project={project} index={index} />
              ))}
            </div>
          )}

          <div className="text-center mt-12">
            <Link
              to="/projects"
              className="inline-flex items-center  "
            >
              <button className="bg-gradient-to-r from-sky-600 via-sky-500 to-sky-700 hover:from-sky-700 hover:via-sky-600 hover:to-sky-800 text-white font-bold px-6 py-3 rounded-2xl shadow-xl shadow-blue-500/25 hover:shadow-blue-500/40 hover:scale-[1.03] active:scale-95 transition-all duration-500 group text-lg flex items-center mx-auto">
                {t('home.featured.viewAll')}
                <ArrowRight className="ml-4 h-6 w-6 transition-transform group-hover:translate-x-2 drop-shadow-[0_0_8px_rgba(255,255,255,0.5)]" />
              </button>
            </Link>

          </div>
        </div>
      </section>

      {/* About/Skills Quick Section */}
      <section className="py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="bg-opacity-15 bg-sky-800 rounded-xl shadow-md p-8"
          >
            <h3 className="text-2xl font-bold text-sky-800 mb-4">
              {t('home.aboutQuick.title')}
            </h3>
            <p className="text-white mb-6">
              {t('home.aboutQuick.text')}
            </p>
            <Link
              to="/about"
              className="flex items-center text-sky-600 hover:text-sky-700 
         font-medium transition duration-300 
         hover:shadow-xl hover:scale-105 
         rounded-lg px-4 py-2 cursor-pointer mt-8"
            >
              {t('home.aboutQuick.link')}
              <ArrowRight size={18} className="ml-2" />
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="bg-opacity-15 bg-sky-800 rounded-xl shadow-md p-8"
          >
            <h3 className="text-2xl font-bold text-sky-800 mb-4">
              {t('home.expertiseQuick.title')}
            </h3>
            <ul className="space-y-3 text-white">
              <li className="flex items-start">
                <span className="inline-flex items-center justify-center w-6 h-6 bg-sky-100 text-sky-700 rounded-full mr-3">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </span>
                {t('home.expertiseQuick.frontend')}
              </li>
              <li className="flex items-start">
                <span className="inline-flex items-center justify-center w-6 h-6 bg-sky-100 text-sky-700 rounded-full mr-3">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </span>
                {t('home.expertiseQuick.backend')}
              </li>
              <li className="flex items-start">
                <span className="inline-flex items-center justify-center w-6 h-6 bg-sky-100 text-sky-700 rounded-full mr-3">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </span>
                {t('home.expertiseQuick.uiux')}
              </li>
              <li className="flex items-start">
                <span className="inline-flex items-center justify-center w-6 h-6 bg-sky-100 text-sky-700 rounded-full mr-3">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </span>
                {t('home.expertiseQuick.api')}
              </li>
            </ul>
            <Link
              to="/skills"
              className="flex items-center text-sky-600 hover:text-sky-700 
         font-medium transition duration-300 
         hover:shadow-xl hover:scale-105 
         rounded-lg px-4 py-2 cursor-pointer mt-6"
            >
              {t('home.expertiseQuick.link')}
              <ArrowRight size={18} className="ml-2" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 0.9, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="bg-gradient-to-r from-sky-300 to-sky-700 bg-opacity-35 rounded-2xl p-8 md:p-12 text-center md:mx-24"
        >
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            {t('home.cta.title')}
          </h2>
          <p className="text-white max-w-2xl mx-auto mb-8">
            {t('home.cta.text')}
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center"
          >
            <button
              className="bg-gradient-to-r from-primary-200 via-white to-primary-300 hover:from-primary-300 hover:via-primary-100 hover:to-primary-400 text-sky-600 font-bold px-7 py-3 rounded-2xl shadow-xl  hover:shadow-sky-700 hover:scale-[1.03] active:scale-95 transition-all duration-500 group text-lg flex items-center mx-auto">
              {t('home.cta.button')}
              <ArrowRight className="ml-4 h-6 w-6 transition-transform group-hover:translate-x-2 drop-shadow-[0_0_8px_rgba(255,255,255,0.5)]" />
            </button>
          </Link>
        </motion.div>
      </section>
    </>
  );
};

export default HomePage;